import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../../components/AdminLayout';

interface Product {
  _id: string;
  name: string;
  brand: string;
  description: string;
  price: number;
  salePrice?: number;
  images: string[];
  sizes: string[];
  colors: string[];
  collectionId?: { _id: string; name: string };
  category: string;
  stock: number;
  isActive: boolean;
  isFeatured: boolean;
  freeDelivery: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Collection {
  _id: string;
  name: string;
}

const AdminProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    description: '',
    price: '',
    salePrice: '',
    stock: '10',
    images: [] as string[],
    sizes: [] as string[],
    colors: [] as string[],
    category: 'other',
    collectionId: '',
    isActive: true,
    isFeatured: false,
    freeDelivery: false
  });
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Lightbox state
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);

  const openLightbox = (images: string[], index: number) => {
    setLightbox({ images, index });
  };

  const closeLightbox = () => setLightbox(null);

  const lightboxPrev = useCallback(() => {
    if (!lightbox) return;
    setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.images.length) % lightbox.images.length });
  }, [lightbox]);

  const lightboxNext = useCallback(() => {
    if (!lightbox) return;
    setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.images.length });
  }, [lightbox]);

  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox, lightboxPrev, lightboxNext]);

  const categories = [
    { value: 'jeans', label: 'Jeans' },
    { value: 'polos', label: 'Polos' },
    { value: 'sweaters', label: 'Sweaters' },
    { value: 'trousers', label: 'Trousers' },
    { value: 'jackets', label: 'Jackets' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'shoes', label: 'Shoes' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    fetchProducts();
    fetchCollections();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/products/admin/all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) setProducts(data.data.products);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await fetch('/api/collections');
      const data = await response.json();
      if (data.success) setCollections(data.data);
    } catch {
      // silently fail
    }
  };

  // Convert any image (including iOS HEIC) to JPEG blob via canvas
  const compressImage = (file: File, maxWidth = 1200, quality = 0.85): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        const scale = Math.min(1, maxWidth / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas not supported'));
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Compression failed'))),
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (files: File[]) => {
    setUploading(true);
    const uploadedPaths: string[] = [];

    try {
      for (const file of files) {
        // Compress / convert to JPEG before uploading
        let blob: Blob;
        try {
          blob = await compressImage(file);
        } catch {
          toast.error(`Could not process ${file.name}`);
          continue;
        }

        const fd = new FormData();
        const safeName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
        fd.append('image', blob, safeName);

        const token = localStorage.getItem('adminToken');
        const response = await fetch('/api/upload/image', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: fd
        });

        const data = await response.json();
        if (data.success) {
          uploadedPaths.push(data.data.url);
        } else {
          throw new Error(data.message || 'Upload failed');
        }
      }
      
      if (uploadedPaths.length > 0) {
        setSuccessMessage(`${uploadedPaths.length} image${uploadedPaths.length > 1 ? 's' : ''} uploaded successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
      
      return uploadedPaths;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error('Failed to upload images: ' + msg);
      return [];
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let imageUrls: string[] = [];
    
    // First, get existing images that are not empty
    const existingImages = formData.images.filter(img => img && img.trim() !== '');
    imageUrls = [...existingImages];
    
    // Upload new images if selected
    if (imageFiles.length > 0) {
      const uploadedPaths = await handleImageUpload(imageFiles);
      if (uploadedPaths.length === 0 && imageUrls.length === 0) {
        toast.error('Please upload at least one image');
        return;
      }
      imageUrls = [...imageUrls, ...uploadedPaths];
    }

    if (imageUrls.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    const productData = {
      ...formData,
      price: Number(formData.price),
      salePrice: formData.salePrice ? Number(formData.salePrice) : undefined,
      stock: Number(formData.stock) || 0,
      images: imageUrls,
      sizes: formData.sizes.filter(size => size && size.trim() !== ''),
      colors: formData.colors.filter(color => color && color.trim() !== ''),
      category: formData.category,
      collectionId: formData.collectionId || undefined,
      isFeatured: formData.isFeatured,
      freeDelivery: formData.freeDelivery
    };

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingProduct 
        ? `/api/products/${editingProduct._id}`
        : '/api/products';
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productData)
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchProducts();
        resetForm();
        toast.success(editingProduct ? 'Product updated!' : 'Product created!');
      } else {
        if (data.details && Array.isArray(data.details)) {
          const errorMessages = data.details.map((detail: any) => `${detail.field}: ${detail.message}`).join(', ');
          toast.error(`Validation: ${errorMessages}`);
        } else {
          toast.error(data.message || 'Failed to save product');
        }
      }
    } catch {
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      brand: product.brand,
      description: product.description,
      price: product.price.toString(),
      salePrice: product.salePrice?.toString() || '',
      stock: product.stock?.toString() || '0',
      images: product.images,
      sizes: product.sizes,
      colors: product.colors,
      category: product.category || 'other',
      collectionId: product.collectionId?._id || '',
      isActive: product.isActive,
      isFeatured: product.isFeatured || false,
      freeDelivery: product.freeDelivery || false
    });
    setShowForm(true);
  };

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        await fetchProducts();
        toast.success('Product deleted');
      } else {
        toast.error(data.message || 'Failed to delete product');
      }
    } catch {
      toast.error('Failed to delete product');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      description: '',
      price: '',
      salePrice: '',
      stock: '10',
      images: [],
      sizes: [],
      colors: [],
      category: 'other',
      collectionId: '',
      isActive: true,
      isFeatured: false,
      freeDelivery: false
    });
    setImageFiles([]);
    setEditingProduct(null);
    setShowForm(false);
  };

  // Filter products based on search and status
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && product.isActive) ||
                         (filterStatus === 'inactive' && !product.isActive);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout title="Products">
        <div className="flex items-center justify-center py-32">
          <div className="spinner-lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Products"
      subtitle={`${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''}`}
      actions={
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-offblack text-offwhite px-4 py-2 text-sm font-medium tracking-wide hover:bg-charcoal transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Product
        </button>
      }
    >

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-warmgray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-warmgray-200 text-sm text-offblack placeholder:text-warmgray-400 focus:outline-none focus:border-offblack transition-colors"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-white border border-warmgray-200 px-4 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack sm:w-36"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {filteredProducts.map((product) => (
          <div key={product._id} className="bg-white border border-warmgray-200 overflow-hidden group">
            {/* Image */}
            <div className="relative aspect-[3/4] overflow-hidden bg-warmgray-100">
              <img
                src={product.images[0] || '/api/placeholder/300/300'}
                alt={product.name}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => product.images.length > 0 && openLightbox(product.images, 0)}
              />
              {/* Badges */}
              <div className="absolute top-2 left-2 flex gap-1">
                {product.salePrice && (
                  <span className="bg-red-600 text-white text-[0.65rem] tracking-wider uppercase px-2 py-0.5">Sale</span>
                )}
                {product.isFeatured && (
                  <span className="bg-offblack text-offwhite text-[0.65rem] tracking-wider uppercase px-2 py-0.5">Featured</span>
                )}
                {product.images.length > 1 && (
                  <span className="bg-black/60 text-white text-[0.65rem] px-2 py-0.5 backdrop-blur-sm">
                    {product.images.length} photos
                  </span>
                )}
              </div>
              {/* Actions */}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleEdit(product)}
                  className="p-1.5 bg-white shadow-sm hover:bg-warmgray-100 transition-colors"
                  aria-label={`Edit ${product.name}`}
                >
                  <PencilIcon className="w-3.5 h-3.5 text-offblack" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(product._id)}
                  className="p-1.5 bg-white shadow-sm hover:bg-red-50 transition-colors"
                  aria-label={`Delete ${product.name}`}
                >
                  <TrashIcon className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="p-4">
              <p className="text-[0.65rem] tracking-wider uppercase text-warmgray-500 mb-0.5">{product.brand}</p>
              <h3 className="text-sm font-medium text-offblack truncate">{product.name}</h3>

              <div className="flex items-center justify-between mt-2">
                <div>
                  {product.salePrice ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-red-600">{product.salePrice.toLocaleString()} DA</span>
                      <span className="text-xs text-warmgray-400 line-through">{product.price.toLocaleString()} DA</span>
                    </div>
                  ) : (
                    <span className="text-sm font-medium text-offblack">{product.price.toLocaleString()} DA</span>
                  )}
                </div>
                <span className={`text-[0.65rem] tracking-wide uppercase px-2 py-0.5 ${
                  product.isActive ? 'bg-green-50 text-green-700' : 'bg-warmgray-100 text-warmgray-500'
                }`}>
                  {product.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-warmgray-100">
                <span className="text-xs text-warmgray-500">
                  {product.sizes.length} sizes · {product.colors.length} colors
                </span>
                <span className={`text-xs ${
                  product.stock > 10 ? 'text-green-600' : product.stock > 0 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                </span>
              </div>

              {product.collectionId && (
                <p className="text-[0.6rem] text-warmgray-400 mt-1.5">Collection: {product.collectionId.name}</p>
              )}
            </div>
          </div>
        ))}
        </div>

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-16">
          <PhotoIcon className="w-16 h-16 mx-auto text-warmgray-300 mb-3" />
          <h3 className="text-lg font-display text-offblack mb-1">No products found</h3>
          <p className="text-sm text-warmgray-500 mb-6">
            {searchTerm ? 'Try a different search term' : 'Start by adding your first product'}
          </p>
          <button onClick={() => setShowForm(true)} className="bg-offblack text-offwhite px-6 py-2.5 text-sm tracking-wider uppercase hover:bg-charcoal transition-colors">
            Add Product
          </button>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white p-6 max-w-sm w-full mx-4">
            <h3 className="font-display text-lg text-offblack mb-2">Delete Product</h3>
            <p className="text-sm text-warmgray-600 mb-6">Are you sure? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-600 text-white py-2 text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-warmgray-200 py-2 text-sm font-medium text-offblack hover:bg-warmgray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white max-w-3xl w-full mx-4 p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-offblack">
                {editingProduct ? 'Edit Product' : 'New Product'}
              </h2>
              <button onClick={resetForm} className="p-1 hover:bg-warmgray-100 transition-colors">
                <XMarkIcon className="w-5 h-5 text-warmgray-500" />
              </button>
            </div>
              
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="prod-name" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Name *</label>
                    <input id="prod-name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="Product name" />
                  </div>
                  <div>
                    <label htmlFor="prod-brand" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Brand *</label>
                    <input id="prod-brand" type="text" required value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="Brand name" />
                  </div>
                </div>

                <div>
                  <label htmlFor="prod-desc" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Description *</label>
                  <textarea id="prod-desc" required value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack h-24 resize-none focus:outline-none focus:border-offblack transition-colors" placeholder="Product description" />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="prod-price" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Price (DA) *</label>
                    <input id="prod-price" type="number" required value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="0" />
                  </div>
                  <div>
                    <label htmlFor="prod-sale" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Sale Price</label>
                    <input id="prod-sale" type="number" value={formData.salePrice} onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="0" />
                  </div>
                  <div>
                    <label htmlFor="prod-stock" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Stock *</label>
                    <input id="prod-stock" type="number" required min="0" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="10" />
                  </div>
                  <div>
                    <label htmlFor="prod-collection" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Collection</label>
                    <select id="prod-collection" value={formData.collectionId} onChange={(e) => setFormData({ ...formData, collectionId: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors bg-white">
                      <option value="">None</option>
                      {collections.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="prod-category" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Category</label>
                  <select id="prod-category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors bg-white">
                    {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                  </select>
                </div>

                {/* Images */}
                <div>
                  <label className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Images</label>
                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {formData.images.map((img, i) => (
                        <div key={i} className="relative group/img aspect-square bg-warmgray-100">
                          <img src={img} alt={`Product ${i + 1}`} className="w-full h-full object-contain" />
                          <button type="button" onClick={() => setFormData({ ...formData, images: formData.images.filter((_, idx) => idx !== i) })}
                            className="absolute top-0.5 right-0.5 p-0.5 bg-red-600 text-white opacity-0 group-hover/img:opacity-100 transition-opacity">
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="border border-dashed border-warmgray-300 p-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
                      multiple
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files || []);
                        if (newFiles.length > 0) {
                          setImageFiles((prev) => [...prev, ...newFiles]);
                        }
                        // Reset so the same file can be re-selected
                        e.target.value = '';
                      }}
                      className="sr-only"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer block text-center w-full"
                    >
                      <PhotoIcon className="w-8 h-8 text-warmgray-400 mx-auto mb-2" />
                      <p className="text-sm text-warmgray-500">
                        {imageFiles.length > 0 ? `${imageFiles.length} file(s) selected` : 'Tap to upload'}
                      </p>
                    </button>
                    {imageFiles.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mt-3">
                        {imageFiles.map((file, i) => (
                          <div key={i} className="relative group/img aspect-square bg-warmgray-100 border border-green-200">
                            <img src={URL.createObjectURL(file)} alt={`New ${i + 1}`} className="w-full h-full object-contain" />
                            <button type="button" onClick={() => setImageFiles(imageFiles.filter((_, idx) => idx !== i))}
                              className="absolute top-0.5 right-0.5 p-0.5 bg-red-600 text-white opacity-0 group-hover/img:opacity-100 transition-opacity">
                              <TrashIcon className="w-3 h-3" />
                            </button>
                            <span className="absolute bottom-0.5 left-0.5 text-[0.5rem] bg-green-600 text-white px-1">New</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {successMessage && (
                  <div className="bg-green-50 border border-green-200 p-3">
                    <p className="text-green-700 text-sm">{successMessage}</p>
                  </div>
                )}

                {/* Sizes & Colors */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="prod-sizes" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Sizes (comma separated)</label>
                    <input id="prod-sizes" type="text" value={formData.sizes.join(', ')}
                      onChange={(e) => setFormData({ ...formData, sizes: e.target.value.split(',').map(s => s.trim()) })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="39, 40, 41, 42, 43..." />
                  </div>
                  <div>
                    <label htmlFor="prod-colors" className="block text-xs tracking-wider uppercase text-warmgray-500 mb-1.5">Colors (comma separated)</label>
                    <input id="prod-colors" type="text" value={formData.colors.join(', ')}
                      onChange={(e) => setFormData({ ...formData, colors: e.target.value.split(',').map(c => c.trim()) })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors" placeholder="Black, White, Red..." />
                  </div>
                </div>

                {/* Toggles */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4 accent-offblack" />
                    <span className="text-sm text-offblack">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.isFeatured} onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                      className="w-4 h-4 accent-offblack" />
                    <span className="text-sm text-offblack">Featured</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.freeDelivery} onChange={(e) => setFormData({ ...formData, freeDelivery: e.target.checked })}
                      className="w-4 h-4 accent-green-600" />
                    <span className="text-sm text-offblack">Free Delivery</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.category === 'shoes'}
                      onChange={(e) => setFormData({ ...formData, category: e.target.checked ? 'shoes' : 'other' })}
                      className="w-4 h-4 accent-offblack" />
                    <span className="text-sm text-offblack">Shoes <span className="text-xs text-warmgray-400">(hides size guide)</span></span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={uploading}
                    className="flex-1 bg-offblack text-offwhite py-2.5 text-sm font-medium tracking-wide hover:bg-charcoal transition-colors disabled:opacity-50">
                    {uploading ? 'Uploading...' : editingProduct ? 'Update Product' : 'Create Product'}
                  </button>
                  <button type="button" onClick={resetForm}
                    className="flex-1 border border-warmgray-200 py-2.5 text-sm font-medium text-offblack hover:bg-warmgray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={closeLightbox}>
          <button className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10" onClick={closeLightbox}>
            <XMarkIcon className="w-6 h-6 text-white" />
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-1 rounded-full">
            {lightbox.index + 1} / {lightbox.images.length}
          </div>
          {lightbox.images.length > 1 && (
            <button className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}>
              <ChevronLeftIcon className="w-7 h-7 text-white" />
            </button>
          )}
          <img src={lightbox.images[lightbox.index]} alt={`Preview ${lightbox.index + 1}`}
            className="max-h-[80vh] max-w-[85vw] object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
          {lightbox.images.length > 1 && (
            <button className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              onClick={(e) => { e.stopPropagation(); lightboxNext(); }}>
              <ChevronRightIcon className="w-7 h-7 text-white" />
            </button>
          )}
          {lightbox.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] px-4" onClick={(e) => e.stopPropagation()}>
              {lightbox.images.map((img, idx) => (
                <img key={idx} src={img} alt={`Thumb ${idx + 1}`} onClick={() => setLightbox({ ...lightbox, index: idx })}
                  className={`h-14 w-14 object-contain bg-warmgray-900 flex-shrink-0 cursor-pointer transition-all border-2 ${
                    idx === lightbox.index ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                  }`} />
              ))}
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProducts;
